import { ArrowUpRight, Clapperboard, ListChecks, MessageSquareText, Send, Video } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MosaicoAstral } from '../../components/branding';
import { useI18n } from '../../i18n/I18nProvider';
import { cn } from '../../lib/cn';

// La home como panel de control (ciclo-panel, R1): la única página que se le
// da a Ailen. Cuatro accesos grandes, en el orden que fijó Javier, y
// Sin datos, sin tablas: botones. Sistema (solo admin) va por la barra, no por acá.
// ciclo-tareas, R6: Leads (Notion) se reemplaza por Tareas — acceso interno, sin rol.
// ciclo-emisor (19/10/2026): quinto acceso, Campañas; grilla de 6 con un lugar de reserva.

export const URL_MEET = 'https://meet.metamorfosisorigen.com';

interface Acceso {
  id: 'ventas' | 'publicar' | 'reuniones' | 'tareas' | 'campanas';
  to: string;
  externo: boolean;
  icon: ReactNode;
  // Cada acceso tiene su tinte; los cuatro salen de la paleta de acentos (tokens.css).
  tono: string;
}

const ACCESOS: Acceso[] = [
  {
    id: 'ventas',
    to: '/bandeja',
    externo: false,
    icon: <MessageSquareText size={34} />,
    tono: 'from-marca-primario to-marca-primario-claro',
  },
  {
    id: 'publicar',
    to: '/publicar',
    externo: false,
    icon: <Clapperboard size={34} />,
    tono: 'from-marca-naranja to-marca-amarillo',
  },
  {
    id: 'reuniones',
    to: URL_MEET,
    externo: true,
    icon: <Video size={34} />,
    tono: 'from-marca-azul to-marca-azul-claro',
  },
  {
    id: 'tareas',
    to: '/tareas',
    externo: false,
    icon: <ListChecks size={34} />,
    tono: 'from-success to-marca-azul-claro',
  },
  {
    id: 'campanas',
    to: '/campanas',
    externo: false,
    icon: <Send size={34} />,
    tono: 'from-marca-amarillo to-marca-primario',
  },
];

export function InicioPage(): JSX.Element {
  const t = useI18n().inicio;

  return (
    // Los accesos centrados en la pantalla. El mosaico va solo de fondo, el mismo
    // que el login, para que las dos pantallas se sientan de la misma app.
    // 19/10/2026 (Javier): grilla de 6 lugares (3 x 2), 5 accesos y un lugar de
    // reserva, invisible, para una función futura. Así ninguno queda descolgado.
    <div className="relative flex h-[calc(100vh-7.5rem)] items-center justify-center overflow-hidden px-6">
      <MosaicoAstral className="pointer-events-none absolute inset-0 z-0 select-none text-black/[0.07] dark:text-white/[0.09]" />
      <div className="relative z-10 grid w-full max-w-5xl grid-cols-3 gap-5">
        {ACCESOS.map((a) => (
          <AccesoGrande
            key={a.id}
            acceso={a}
            titulo={t.accesos[a.id].titulo}
            detalle={t.accesos[a.id].detalle}
            pestana={t.abrirEnPestana}
          />
        ))}
        {/* El sexto lugar: reservado. Ocupa su celda para que la grilla quede pareja. */}
        <div aria-hidden="true" className="invisible" data-reserva="acceso-futuro" />
      </div>
    </div>
  );
}

function AccesoGrande({
  acceso,
  titulo,
  detalle,
  pestana,
}: {
  acceso: Acceso;
  titulo: string;
  detalle: string;
  pestana: string;
}): JSX.Element {
  const clase = cn(
    'group relative flex aspect-[4/3] flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br p-5 text-white shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-marca-primario/40',
    acceso.tono,
  );
  const contenido = (
    <>
      <span className="flex items-start justify-between">
        <span className="rounded-xl bg-white/20 p-3 backdrop-blur-sm">{acceso.icon}</span>
        {acceso.externo && <ArrowUpRight size={20} className="text-white/70" aria-label={pestana} />}
      </span>
      <span>
        <span className="block font-titulos text-2xl font-bold leading-tight">{titulo}</span>
        <span className="mt-1 block text-sm text-white/85">{detalle}</span>
      </span>
    </>
  );
  return acceso.externo ? (
    <a href={acceso.to} target="_blank" rel="noopener noreferrer" className={clase} title={pestana}>
      {contenido}
    </a>
  ) : (
    <Link to={acceso.to} className={clase}>
      {contenido}
    </Link>
  );
}
